import type { GameState } from '../game/tuning';

export interface HudCallbacks {
  onStart(): void;
  onResume(): void;
  onRestart(): void;
  onPause(): void;
  onToggleMute(): boolean;
}

/**
 * DOM HUD + full-screen state overlays. Reads game state; never owns rules.
 * Numeric fields use fixed-width font features so values do not jitter.
 */
export class Hud {
  private readonly scoreValue = this.el('#score-value');
  private readonly bestValue = this.el('#best-value');
  private readonly speedValue = this.el('#speed-value');
  private readonly distanceValue = this.el('#distance-value');
  private readonly overdriveFill = this.el('#overdrive-fill');
  private readonly overdriveLabel = this.el('#overdrive-label');
  private readonly popup = this.el('#popup');
  private readonly hudRoot = this.el('#hud');
  private readonly screens: Record<string, HTMLElement> = {
    title: this.el('#screen-title'),
    pause: this.el('#screen-pause'),
    gameover: this.el('#screen-gameover'),
  };
  private readonly finalScore = this.el('#final-score');
  private readonly finalDistance = this.el('#final-distance');
  private readonly finalBest = this.el('#final-best');
  private readonly newRecord = this.el('#new-record');
  private popupTimer: number | undefined;
  private meterCrashed = false;

  constructor(callbacks: HudCallbacks) {
    this.bind('#btn-start', callbacks.onStart);
    this.bind('#btn-resume', callbacks.onResume);
    this.bind('#btn-restart', callbacks.onRestart);
    this.bind('#btn-retry', callbacks.onRestart);
    this.bind('#btn-pause', callbacks.onPause);
    const muteButtons = document.querySelectorAll<HTMLButtonElement>('.btn-mute');
    for (const button of muteButtons) {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        const muted = callbacks.onToggleMute();
        for (const b of muteButtons) {
          b.textContent = muted ? 'SOUND OFF' : 'SOUND ON';
          b.setAttribute('aria-pressed', String(muted));
        }
      });
    }
  }

  setState(state: GameState): void {
    const target = state === 'paused' ? 'pause' : state;
    for (const [name, element] of Object.entries(this.screens)) {
      element.classList.toggle('visible', name === target);
    }
    // Overlay screens own title/gameover moments; telemetry stays out of the way.
    this.hudRoot.classList.toggle('hidden', state === 'title' || state === 'gameover');
    document.body.dataset.state = state;
  }

  update(
    score: number,
    best: number,
    speedKmh: number,
    distanceM: number,
    overdrive: number,
    overdriveActive: boolean,
  ): void {
    this.scoreValue.textContent = String(Math.floor(score)).padStart(6, '0');
    this.bestValue.textContent = String(Math.floor(best)).padStart(6, '0');
    this.speedValue.textContent = String(Math.round(speedKmh));
    this.distanceValue.textContent = `${Math.floor(distanceM)}m`;
    // ~250 km/h is the overdrive ceiling; the gauge fills against it.
    this.hudRoot.style.setProperty('--speed-ratio', Math.min(speedKmh / 250, 1).toFixed(3));
    this.overdriveFill.style.transform = `scaleX(${Math.min(overdrive, 1).toFixed(3)})`;
    this.overdriveLabel.textContent = this.meterCrashed
      ? '¡IMPACTO!'
      : overdriveActive
        ? 'OVERDRIVE ×2'
        : 'DRIVE';
    this.overdriveFill.parentElement?.classList.toggle('active', overdriveActive);
  }

  setMeterCrashed(crashed: boolean): void {
    this.meterCrashed = crashed;
  }

  showGameOver(score: number, distanceM: number, best: number, isRecord: boolean): void {
    this.finalScore.textContent = String(Math.floor(score)).padStart(6, '0');
    this.finalDistance.textContent = `${Math.floor(distanceM)} m`;
    this.finalBest.textContent = String(Math.floor(best)).padStart(6, '0');
    this.newRecord.style.display = isRecord ? 'block' : 'none';
  }

  showPopup(text: string, tone: 'bonus' | 'alert' = 'bonus'): void {
    this.popup.textContent = text;
    this.popup.dataset.tone = tone;
    this.popup.classList.remove('pop');
    // Force reflow so the animation restarts.
    void this.popup.offsetWidth;
    this.popup.classList.add('pop');
    window.clearTimeout(this.popupTimer);
    this.popupTimer = window.setTimeout(() => this.popup.classList.remove('pop'), 900);
  }

  punchScore(): void {
    this.scoreValue.animate(
      [{ transform: 'scale(1.18)' }, { transform: 'scale(1)' }],
      { duration: 130, easing: 'ease-out' },
    );
  }

  flash(): void {
    const overlay = this.el('#flash-overlay');
    overlay.animate([{ opacity: 0.75 }, { opacity: 0 }], { duration: 110, easing: 'ease-out' });
  }

  private bind(selector: string, handler: () => void): void {
    this.el(selector).addEventListener('click', (event) => {
      event.stopPropagation();
      handler();
    });
  }

  private el(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing HUD element: ${selector}`);
    return element;
  }
}
